/**
 * Owner: Masa Bayu
 * File: hub-session.ts
 * Date:Time: 2026-10-22 04:40
 * Description: §457 F3 — klien identiti qxp-hub untuk qxread yang KEKAL bentuk
 *   `AuthSessionView` sebagai perisai: 13 pemakai + 4 fail ujian tidak dibongkar
 *   pada fasa ini, hanya sumber datanya berpindah daripada `/backend/*` (projek
 *   luar) kepada `/v1/accounts/*` (hub).
 *
 *   Penyimpanan: token masuk ke stor sesi hub (`hub-auth.ts`), BUKAN ke stor
 *   identiti asing — supaya kunci luar itu boleh dimadam bersih tanpa
 *   meninggalkan sesi separa. Status reaktif (`anonymous|signed-in|expired`) datang percuma.
 *
 *   Dua pemetaan keputusan (boleh dibatalkan atas arahan Owner):
 *   1. `role` hub → `roles: string[]` kerana pemakai (`reader-profile-data.ts`,
 *      `use-account-lifecycle.ts`) menguji `.includes('author')`; maka
 *      publisher → ['publisher','author'], admin → ['admin'] (lihat `rolesOf`).
 *   2. `logoutSession()` tidak memanggil rangkaian: hub belum ada laluan
 *      rampasan sesi, jadi ia hanya membuang sesi setempat.
 */

import { getHubAuthToken, setHubAuth, setHubAuthRole } from '../publication/hub-auth';
import { endHubSession, expireHubSession, qxpHubOrigin } from '../publication/hub-sync';
import type { AuthCredentials, AuthRegisterInput, AuthSessionView } from './session-types';

export type HubAccountRole = 'reader' | 'publisher';

/**
 * Salinan tambahan pada medan yang sudah ada — pemakai membaca ikut kunci.
 * `| undefined` diperlukan oleh `exactOptionalPropertyTypes` repo ini.
 */
export type HubSessionView = AuthSessionView & {
  provider?: string | undefined;
  createdUser?: boolean | undefined;
  hasPassword?: boolean | undefined;
  googleLinked?: boolean | undefined;
};

export type HubRegisterInput = AuthRegisterInput & { readonly role?: HubAccountRole };

type FetchInit = {
  readonly method?: string;
  readonly headers?: Record<string, string>;
  readonly body?: string;
};
type FetchLike = (input: string, init?: FetchInit) => Promise<Response>;

export class HubSessionError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'HubSessionError';
  }
}

const JSON_HEADERS: Record<string, string> = { 'content-type': 'application/json' };

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function flag(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/** Body ralat hub = { ok:false, error:'…' }; selain itu mesej generik. */
async function messageFor(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    const message = text(body.error);
    if (message.length > 0) return message;
  } catch {
    /* badan bukan JSON — guna mesej generik di bawah */
  }
  return `Request failed (${String(res.status)})`;
}

async function request(fetchImpl: FetchLike, url: string, init: FetchInit): Promise<Response> {
  try {
    return await fetchImpl(url, init);
  } catch {
    throw new HubSessionError('Pelayan identiti tidak dapat dicapai', 0);
  }
}

async function readJsonBody(res: Response): Promise<Record<string, unknown>> {
  try {
    const body = (await res.json()) as unknown;
    return body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  } catch {
    throw new HubSessionError(`Respons identiti bukan JSON (HTTP ${String(res.status)})`, 500);
  }
}

/**
 * Peranan stor → `roles[]` yang dibaca UI. `admin` MESTI dipertahankan: pemetaan
 * lama memadamnya kepada 'reader', lalu akaun pentadbir QX menerima chrome
 * pembaca (My Library + lencana "Verified reader") — §468.
 */
function rolesOf(record: Record<string, unknown>): string[] {
  const role = text(record.role);
  if (role === 'admin') return ['admin'];
  return role === 'publisher' ? ['publisher', 'author'] : ['reader'];
}

/** Perisai: pandangan akaun hub → bentuk `AuthSessionView` yang dipakai UI. */
function toSessionView(record: Record<string, unknown>, token: string): HubSessionView {
  const provider = text(record.provider);
  return {
    account: {
      user: { id: text(record.accountId), email: text(record.email) },
      profile: { displayName: text(record.displayName) },
      roles: rolesOf(record),
    },
    token,
    provider: provider.length > 0 ? provider : undefined,
    createdUser: flag(record.createdUser),
    hasPassword: flag(record.hasPassword),
    googleLinked: flag(record.googleLinked),
  };
}

/**
 * POST laluan identiti yang memulangkan sesi. Gagal-keras: token + accountId
 * wajib ada sebelum stor sesi ditulis, supaya header tidak pernah paparan
 * 'signed-in' tanpa identiti.
 */
async function postSession(
  fetchImpl: FetchLike,
  origin: string,
  path: string,
  payload: Record<string, unknown>,
): Promise<HubSessionView> {
  const res = await request(fetchImpl, `${origin}${path}`, {
    method: 'POST',
    headers: { ...JSON_HEADERS },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new HubSessionError(await messageFor(res), res.status);
  const record = await readJsonBody(res);
  const token = text(record.token);
  const accountId = text(record.accountId);
  if (record.ok !== true || token.length === 0 || accountId.length === 0) {
    throw new HubSessionError('Respons identiti tiada token atau akaun', 500);
  }
  setHubAuth(token, accountId);
  return toSessionView(record, token);
}

function accountPayload(input: AuthCredentials & { displayName?: string; role?: string }) {
  const payload: Record<string, unknown> = { email: input.email, password: input.password };
  if (typeof input.displayName === 'string') payload.displayName = input.displayName;
  if (typeof input.role === 'string') payload.role = input.role;
  return payload;
}

export async function loginWithPassword(
  input: AuthCredentials,
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView> {
  return postSession(fetchImpl, origin, '/v1/accounts/login', accountPayload(input));
}

export async function registerWithPassword(
  input: HubRegisterInput,
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView> {
  return postSession(fetchImpl, origin, '/v1/accounts/register', accountPayload(input));
}

/**
 * §457 F2 — ID token Google disahkan oleh hub (`POST /v1/accounts/oauth/google`);
 * 401 = token ditolak, 502 = JWKS Google tidak capai (mesej hub dipulangkan apa
 * adanya supaya klien tidak tersalah anggap gangguan dependency sebagai
 * kredential rosak).
 */
export async function exchangeGoogleIdToken(
  input: { readonly idToken: string; readonly role?: HubAccountRole },
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView> {
  const payload: Record<string, unknown> = { idToken: input.idToken };
  if (input.role !== undefined) payload.role = input.role;
  return postSession(fetchImpl, origin, '/v1/accounts/oauth/google', payload);
}

/**
 * Pulihkan identiti daripada token sahaja (`GET /v1/accounts/me`). Tiada token
 * → null; sesi ditolak → sesi ditanda tamat + null. Ralat rangkaian DAN
 * ralat HTTP lain (5xx) dinaikkan — pemanggil bootstrap hook menelan keduanya.
 */
export async function fetchSession(
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView | null> {
  const token = getHubAuthToken();
  if (token === null) return null;
  const res = await request(fetchImpl, `${origin}/v1/accounts/me`, {
    method: 'GET',
    headers: { authorization: `Bearer ${token}` },
  });
  if (res.status === 401 || res.status === 403) {
    expireHubSession();
    return null;
  }
  if (!res.ok) throw new HubSessionError(await messageFor(res), res.status);
  const record = await readJsonBody(res);
  // Gagal-keras: 200 tanpa identiti bukan sesi — jangan pernah paparan
  // 'signed-in' dengan akaun kosong.
  if (text(record.accountId).length === 0) {
    throw new HubSessionError('Respons identiti tiada akaun', 500);
  }
  return toSessionView(record, token);
}

/**
 * Naik taraf akaun sesi semasa (reader → publisher). Kata laluan tidak diminta
 * semula — Bearer yang sama kekal. Bukan daftar e-mel baharu.
 */
export async function becomePublisher(
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView> {
  const token = getHubAuthToken();
  if (token === null) {
    throw new HubSessionError('Sesi identiti tiada', 401);
  }
  const res = await request(fetchImpl, `${origin}/v1/accounts/become-publisher`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new HubSessionError(await messageFor(res), res.status);
  const record = await readJsonBody(res);
  if (record.ok !== true || text(record.accountId).length === 0) {
    throw new HubSessionError('Respons identiti tiada akaun', 500);
  }
  const role = text(record.role);
  if (role.length > 0) setHubAuthRole(role);
  return toSessionView(record, token);
}

/**
 * Log masuk dengan e-mel/kata laluan sedia ada, kemudian naik taraf jika reader.
 */
export async function becomePublisherWithPassword(
  input: AuthCredentials,
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<HubSessionView> {
  const session = await loginWithPassword(input, fetchImpl, origin);
  const roles = session.account?.roles ?? [];
  if (roles.includes('publisher')) return session;
  return becomePublisher(fetchImpl, origin);
}

/**
 * Buang sesi setempat HANYA (log keluar / tukar akaun). §509 F4 — bersama token,
 * grant yang lahir daripada sesi itu dan muatan artifak dalam ingatan juga dibuang;
 * kalau tidak, tab yang sama boleh terus memapar halaman penuh selepas log keluar.
 * HUB TIADA LALUAN RAMPASAN SESI (dibina sebelum §457 F3): token yang dicuri kekal
 * sah di pelayan sehingga `SESSION_TTL_MS` atau sehingga akaun dipadam (§453 prune
 * sesi). Dilaporkan sebagai jurang, bukan disembunyikan oleh fungsi ini.
 */
export function logoutSession(): Promise<void> {
  endHubSession();
  // Promise (bukan void) supaya sepadan tepat dengan `logoutSession()`
  // auth-api.ts yang pemakainya panggil `.finally(...)` — perisai F3.
  return Promise.resolve();
}
